import { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { pdfjs } from 'react-pdf';

import { AppContext } from '../../app-context';
import { QuizContext } from '../quiz-context';
import { AnnotationData } from '../types';
import { dpiRatio, PdfExcludeSubTypes } from '../const';
import { useData } from '../../use-data';
import { REACT_APP_PREGNANCY_UPLOAD_ENDPOINT } from '../../const';

export const useQuizDialog = () => {
  const { setLoading } = useContext(AppContext);
  const { token, pregnancyValues, setPregnancyValues, addNotification, intakeId } = useContext(QuizContext);
  const [pdf, setPdf] = useState<pdfjs.PDFDocumentProxy>(null);
  const [filePath, setFilePath] = useState<string>(null);
  const [numPages, setNumPages] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [annotations, setAnnotations] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const {
    data: {
      keywords: { pregnancyComplete, pregnancyFail },
    },
  } = useData();

  const handleUpload = async (): Promise<boolean> => {
    setIsOpen(false);
    setPageIndex(0);
    setLoading(true);
    try {
      await axios.post(
        `${REACT_APP_PREGNANCY_UPLOAD_ENDPOINT}?fileName=${encodeURIComponent(filePath)}&intakeId=${intakeId}`,
        {
          body: Object.values(pregnancyValues),
        },
        {
          headers: {
            token,
          },
        },
      );
      addNotification({ title: pregnancyComplete, type: 'success' });
    } catch (err) {
      addNotification({ title: pregnancyFail, type: 'error' });
      return false;
    } finally {
      setLoading(false);
    }
    return true;
  };
  const getAnnotations = useCallback(() => {
    let currentPage = null;
    pdf
      .getPage(pageIndex + 1)
      .then(p => {
        currentPage = p;
        return p.getAnnotations();
      })
      .then((data: AnnotationData[]) => {
        const annotations = data.filter(({ subtype, readOnly }) => subtype === PdfExcludeSubTypes.WIDGET && !readOnly);
        setAnnotations(
          annotations.map(({ rect, ...rest }) => ({
            rect: currentPage.getViewport({ scale: dpiRatio }).convertToViewportRectangle(rect),
            ...rest,
          })),
        );
        if (!pregnancyValues[pageIndex]) {
          setPregnancyValues({
            ...pregnancyValues,
            [pageIndex]: {
              data: {
                ...annotations.reduce(
                  (result, { fieldName, fieldValue }) => ({
                    ...result,
                    [fieldName]: fieldValue ? (Array.isArray(fieldValue) ? fieldValue[0] : fieldValue) : '',
                  }),
                  {},
                ),
              },
              pageIndex: pageIndex + 1,
            },
          });
        }
      });
  }, [pdf, pageIndex, pregnancyValues, setPregnancyValues]);

  useEffect(() => {
    if (pdf) {
      getAnnotations();
    }
  }, [pdf, pageIndex, getAnnotations]);

  const onLoadSuccess = (pdf: pdfjs.PDFDocumentProxy, filePath: string) => {
    const { numPages } = pdf;
    setNumPages(numPages);
    setPdf(pdf);
    setFilePath(filePath);
  };

  return {
    numPages,
    setNumPages,
    pageIndex,
    setPageIndex,
    annotations,
    setAnnotations,
    isOpen,
    setIsOpen,
    handleUpload,
    onLoadSuccess,
  };
};
